import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { defer, finalize, firstValueFrom, take, timeout } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { BaseViewComponent } from '../base-view.component';
import { DeviceService, Message } from '../../common/services/device.service';
import { SecureDataService } from '../../common/services/securedata.sevice';
import { ConfirmDialogResult } from '../../common/modules/Common-Components/confirm-popup/confirm-popup.component';

@Component({
    selector: 'app-location-required',
    templateUrl: './location-required.component.html',
    styleUrl: './location-required.component.scss',
    standalone: false
})
export class LocationRequiredComponent extends BaseViewComponent {
  private deviceService = inject(DeviceService);
  private secureDataService = inject(SecureDataService);

  @ViewChild('cancelSecureData') cancelSecureData?: ElementRef<HTMLButtonElement>;

  showCancelDialog = false;
  private cancelDialogResultHandled = false;
  chromeLocationDisabled = false
  firefoxLocationDisabled = false
  edgeLocationDisabled = false
  safariLocationDisabled = false

  protected override onSecureDataChanged(): void { }

  async retryLocation(): Promise<void> {
    this.addBreadcrumb('open_browser_settings_selected');
    this.addBreadcrumb('browser_location_prompt_requested');
    this.spinnerService.setLoading(true, '@LocationRequiredComponent.retryLocation');
    try {
      await this.deviceService.initDevice();
      const locationState = this.deviceService.locationState();
      this.addBreadcrumb('browser_location_allowed', locationState.status);
      const data = await firstValueFrom(this.secureDataService.getStatus());
      if (!data) {
        // Location passed but the link has no active stage yet: wait for the agent, as at start-up.
        this.navigateWaiting();
      }
      else if (data.LocationValidationStatus === 'Allowed') {
        this.navigateToSecureDataType(data.Type);
      }
      else {
        this.showLocationInstructionBasedOnBrowser();
      }
    } catch {
      // The HTTP error interceptor already shows the error; keep the retry available.
    } finally {
      this.spinnerService.setLoading(false, '@LocationRequiredComponent.retryLocation');
    }
  }

  openCancelDialog(): void {
    this.addBreadcrumb('cancel_secure_data_selected');
    this.cancelDialogResultHandled = false;
    this.showCancelDialog = true;
  }

  onCancelDialogResult(result: ConfirmDialogResult): void {
    this.cancelDialogResultHandled = true;
    const confirmed = (result.data as { success?: boolean } | undefined)?.success === true;
    if (!confirmed) {
      this.addBreadcrumb('cancellation_modal_cancelled');
      this.restoreCancelFocus();
      return;
    }

    const currentStatus = this.stateService.CurrentStatus;
    if (!currentStatus) {
      this.restoreCancelFocus();
      this.navigateExpired();
      return;
    }

    this.addBreadcrumb('cancellation_confirmed');
    this.spinnerService.setLoading(true, '@LocationRequiredComponent.cancelSecureData');
    defer(() => this.secureDataService.cancel(currentStatus.Type)).pipe(
      timeout(30000),
      take(1),
      finalize(() => this.spinnerService.setLoading(false, '@LocationRequiredComponent.cancelSecureData'))
    ).subscribe({
      next: () => this.navigateExpired(),
      error: () => {
        this.notificationService.showError(Message.ErrorMessage);
        this.restoreCancelFocus();
      }
    });
  }

  onCancelDialogVisibilityChange(visible: boolean): void {
    this.showCancelDialog = visible;
    if (!visible) {
      if (!this.cancelDialogResultHandled) {
        this.addBreadcrumb('cancellation_modal_cancelled');
      }
      this.cancelDialogResultHandled = false;
      this.restoreCancelFocus();
    }
  }

  private restoreCancelFocus(): void {
    setTimeout(() => this.cancelSecureData?.nativeElement.focus());
  }

  private addBreadcrumb(message: string, result?: string): void {
    Sentry.addBreadcrumb({
      category: 'secure-data-location',
      message,
      data: result ? { result } : undefined,
      level: 'info'
    });
  }

  showLocationInstructionBasedOnBrowser() {
    const browser = this.deviceService.getBrowserInfo();
    const browserType = browser.name;
    if (browserType === 'Chrome') {
      this.chromeLocationDisabled = true
    } else if (browserType === 'Firefox') {
      this.firefoxLocationDisabled = true
    } else if (browserType === 'Edge') {
      this.edgeLocationDisabled = true
    } else if (browserType === 'Safari') {
      this.safariLocationDisabled = true
    } else {
      this.chromeLocationDisabled = true;
    }
  }
}
